import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Mail, User, Lock, ArrowRight, Phone, UserCircle } from 'lucide-react';
import { auth, db } from '../lib/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

interface AccountInfo {
  email: string;
  password: string;
}

const EdumiCheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    fullName: ''
  });

  const paymentId = searchParams.get('payment_id');

  useEffect(() => {
    // Auto-fill form if we have checkout data
    const storedData = localStorage.getItem('subscriptionCheckout');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        // We'll need to collect this info in checkout form
      } catch (err) {
        console.error('Error parsing stored data:', err);
      }
    }
    setLoading(false);
  }, []);

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.phone || !formData.fullName) {
      setError('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    if (!paymentId) {
      setError('ไม่พบข้อมูลการชำระเงิน');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const storedData = localStorage.getItem('subscriptionCheckout');
      const checkoutData = storedData ? JSON.parse(storedData) : {};

      console.log('[EdumiCheckout] Sending welcome email...');

      // Determine source from checkout data or URL
      const source = checkoutData.source || 'edumi'; // Default to 'edumi'

      // Call Cloudflare Worker to send email
      const response = await fetch('https://edumi-welcome-email.edumi-subdomin.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          phone: formData.phone,
          fullName: formData.fullName,
          planName: checkoutData.planName,
          planPrice: checkoutData.planPrice,
          paymentId: paymentId,
          source: source
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        const password = formData.phone.slice(-6);
        
        console.log('[EdumiCheckout] Creating Firebase account...');
        
        try {
          // Create Firebase account
          const userCredential = await createUserWithEmailAndPassword(auth, formData.email, password);
          const user = userCredential.user;
          
          console.log('[EdumiCheckout] Firebase account created:', user.uid);
          
          // Store user data in Firestore
          await setDoc(doc(db, 'users', user.uid), {
            email: formData.email,
            fullName: formData.fullName,
            phone: formData.phone,
            role: 'student',
            createdAt: new Date(),
            paymentId: paymentId,
            planName: checkoutData.planName || 'EduMi Pro',
            planPrice: checkoutData.planPrice || 149,
            source: source,
            provider: 'email',
            emailVerified: false,
          });
          
          console.log('[EdumiCheckout] User document created');
          
          // Sign out the user (they need to login manually)
          await auth.signOut();
          
          setAccountInfo({
            email: formData.email,
            password: password
          });
          
          // Clear checkout data
          localStorage.removeItem('subscriptionCheckout');
        } catch (firebaseError: any) {
          console.error('[EdumiCheckout] Firebase account creation error:', firebaseError);
          
          // If account already exists, still show success (email was sent)
          if (firebaseError.code === 'auth/email-already-in-use') {
            console.log('[EdumiCheckout] Account already exists - showing login info anyway');
            setAccountInfo({
              email: formData.email,
              password: password
            });
            localStorage.removeItem('subscriptionCheckout');
          } else {
            throw firebaseError;
          }
        }
      } else {
        setError(result.error || 'เกิดข้อผิดพลาดในการส่งอีเมล');
      }
    } catch (err: any) {
      console.error('[EdumiCheckout] Error sending email:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการส่งอีเมล');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !accountInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-black flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-400" />
          <p>กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (accountInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-black text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-2xl mx-auto">
            {/* Success Icon */}
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <CheckCircle className="w-16 h-16 text-green-400" />
              </div>
              <h1 className="text-4xl font-bold mb-4">ชำระเงินสำเร็จ!</h1>
              <p className="text-gray-400 text-lg">บัญชีของคุณถูกสร้างเรียบร้อยแล้ว</p>
            </div>

            {/* Account Info Card */}
            <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 backdrop-blur-xl border border-purple-500/50 rounded-2xl p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold">ข้อมูลการเข้าสู่ระบบ</h2>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-gray-900/50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-400">Email (อีเมล)</span>
                  </div>
                  <p className="text-xl font-bold font-mono text-purple-300">{accountInfo.email}</p>
                </div>

                <div className="bg-gray-900/50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Lock className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-400">Password (รหัสผ่าน)</span>
                  </div>
                  <p className="text-2xl font-bold font-mono text-purple-300">{accountInfo.password}</p>
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-yellow-200">
                  <strong>⚠️ สำคัญ:</strong> กรุณาบันทึกข้อมูลนี้ไว้ คุณจะได้รับอีเมลยืนยันพร้อมข้อมูลการเข้าสู่ระบบ
                </p>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-lg shadow-purple-500/50"
              >
                เข้าสู่ระบบเลย
                <ArrowRight size={24} />
              </button>
            </div>

            {/* Email Sent Notice */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 text-center">
              <Mail className="w-12 h-12 mx-auto mb-3 text-blue-400" />
              <p className="text-blue-200">
                เราได้ส่งอีเมลยืนยันพร้อมข้อมูลการเข้าสู่ระบบไปที่ <strong>{formData.email}</strong> แล้ว
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-black text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
            <h1 className="text-4xl font-bold mb-4">ชำระเงินสำเร็จ!</h1>
            <p className="text-gray-400 text-lg">กรุณากรอกข้อมูลเพื่อสร้างบัญชีของคุณ</p>
          </div>

          {/* Form Card */}
          <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 backdrop-blur-xl border border-purple-500/50 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Mail className="w-6 h-6 text-purple-400" />
              <h2 className="text-2xl font-bold">กรอกข้อมูลเพื่อรับ Username และ Password</h2>
            </div>
            <form onSubmit={handleSendEmail} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                  <UserCircle className="w-4 h-4" />
                  ชื่อ-นามสกุล
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-purple-500/30 rounded-xl focus:outline-none focus:border-purple-400 transition-colors text-white placeholder-gray-500"
                  placeholder="กรอกชื่อ-นามสกุล"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  อีเมล
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-purple-500/30 rounded-xl focus:outline-none focus:border-purple-400 transition-colors text-white placeholder-gray-500"
                  placeholder="example@email.com"
                  required
                />
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  ใช้ Email นี้สำหรับเข้าสู่ระบบ
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  เบอร์โทรศัพท์
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-purple-500/30 rounded-xl focus:outline-none focus:border-purple-400 transition-colors text-white placeholder-gray-500"
                  placeholder="0812345678"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  required
                />
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  รหัสผ่านของคุณจะเป็น 6 ตัวท้ายของเบอร์โทร
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-lg shadow-purple-500/50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    กำลังสร้างบัญชี...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    ส่งข้อมูลการเข้าสู่ระบบไปที่อีเมล
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EdumiCheckoutSuccess;
